import { WorkCategory } from '../services/messageQueue.service';

export interface ReviewableWorkItem {
	userId: number | null;
	projectId: number | null;
	clientId: number | null;
	precinctId: number | null;
	blockId: number | null;
	blockLevelFrom: number | null;
	blockLevelTo: number | null;
	blockUnitFrom: number | null;
	blockUnitTo: number | null;
	blockUnitNumberPattern: 'continuous' | 'odd' | 'even' | null;
	typeId: number | null;
	subTypeId: number | null;
	subTypeValueId: number | null;
	level: number | null;
	unit: number | null;
	quantity: number | null;
	quantityUnit: string;
	performedAt: string;
	workCategory: WorkCategory;
	isClientBillable: boolean;
	isWorkerCreditable: boolean;
	reportedLocationText: string;
	helperUserIds: number[];
}

export interface WorkItemReviewOptions {
	/** False only when the selected project has no precincts configured. */
	requireStructuredLocation?: boolean;
}

/**
 * Form fields a review issue can be attributed to, so the review dialog can
 * put a red border on the specific control(s) that need attention instead
 * of only showing the "Please correct: ..." summary line.
 */
export type WorkItemIssueField =
	| 'userId'
	| 'projectId'
	| 'clientId'
	| 'typeId'
	| 'subTypeId'
	| 'subTypeValueId'
	| 'quantity'
	| 'quantityUnit'
	| 'performedAt'
	| 'precinctId'
	| 'blockId'
	| 'level'
	| 'unit'
	| 'reportedLocationText'
	| 'workCategory';

export interface WorkItemReviewIssue {
	/** Which field(s) this issue applies to; empty when no single control owns it. */
	fields: WorkItemIssueField[];
	/** Full text for the bottom "Please correct" summary line. */
	message: string;
	/**
	 * Short text for that field's own helper text, when the full `message`
	 * would be too long to repeat under every one of several fields it
	 * applies to. Falls back to `message` when not set.
	 */
	fieldHint?: string;
}

const BLOCK_MISMATCH_HINT = "Level and Unit doesn't belongs to this Block, Please review it";

export const CATEGORY_DEFAULTS: Record<WorkCategory, { isClientBillable: boolean; isWorkerCreditable: boolean }> = {
	normal: { isClientBillable: true, isWorkerCreditable: true },
	free_redo: { isClientBillable: false, isWorkerCreditable: true },
	rectify: { isClientBillable: false, isWorkerCreditable: true },
	fabrication: { isClientBillable: false, isWorkerCreditable: true },
};

const FIELD_LABELS: Array<[keyof ReviewableWorkItem, string]> = [
	['userId', 'credited worker'],
	['projectId', 'project'],
	['clientId', 'client'],
	['typeId', 'type'],
	['subTypeId', 'sub-type'],
	['subTypeValueId', 'value'],
];

/**
 * Same validation as before, restructured to tag each issue with the form
 * field(s) it applies to. `getWorkItemReviewIssues` (string messages, for
 * the "Please correct" summary line) and `getWorkItemInvalidFields` (which
 * controls get a red border) are both derived from this single source of
 * truth so the two can never drift apart.
 */
export const getWorkItemFieldIssues = (
	item: ReviewableWorkItem,
	options: WorkItemReviewOptions = {}
): WorkItemReviewIssue[] => {
	const issues: WorkItemReviewIssue[] = FIELD_LABELS.filter(([key]) => item[key] === null).map(([key, label]) => ({
		fields: [key as WorkItemIssueField],
		message: `Missing ${label}`,
	}));

	if (item.quantity === null || !Number.isFinite(item.quantity) || item.quantity <= 0) {
		issues.push({ fields: ['quantity'], message: 'Quantity must be greater than zero' });
	}
	if (!item.quantityUnit.trim()) issues.push({ fields: ['quantityUnit'], message: 'Missing quantity unit' });
	if (item.quantityUnit.length > 32) {
		issues.push({ fields: ['quantityUnit'], message: 'Quantity unit must be 32 characters or fewer' });
	}
	if (!item.performedAt || Number.isNaN(new Date(item.performedAt).getTime())) {
		issues.push({ fields: ['performedAt'], message: 'Missing or invalid performed date' });
	}

	const hasExactLocation =
		item.precinctId !== null && item.blockId !== null && item.level !== null && item.unit !== null;
	if ((options.requireStructuredLocation ?? true) && !hasExactLocation) {
		// Highlight only the specific location fields that are actually empty,
		// not all four indiscriminately.
		const missingLocationFields = (['precinctId', 'blockId', 'level', 'unit'] as const).filter(
			(key) => item[key] === null
		);
		issues.push({
			fields: missingLocationFields,
			message: 'Location requires precinct, block, level and unit',
		});
	}
	if (item.reportedLocationText.length > 1000) {
		issues.push({
			fields: ['reportedLocationText'],
			message: 'Reported location must be 1000 characters or fewer',
		});
	}
	const levelFrom = item.blockLevelFrom ?? 1;
	if (item.level !== null && item.level < levelFrom) {
		issues.push({
			// A photo/caption-derived level that doesn't fit the selected block
			// usually means the WORKER picked the wrong block, not that the
			// level reading itself is wrong — highlight both the value and the
			// block it was checked against, and say so instead of implying the
			// level value should simply be edited to fit. The full explanation
			// stays in the bottom summary; each field gets the short hint so it
			// isn't repeated three times on screen.
			fields: ['level', 'blockId'],
			message: `Level ${item.level} doesn't match the selected block's range (${levelFrom}-${
				item.blockLevelTo ?? 'the configured maximum'
			}) — check the block`,
			fieldHint: BLOCK_MISMATCH_HINT,
		});
	} else if (item.level !== null && item.blockLevelTo !== null && item.level > item.blockLevelTo) {
		issues.push({
			fields: ['level', 'blockId'],
			message: `Level ${item.level} doesn't match the selected block's range (${levelFrom}-${item.blockLevelTo}) — check the block`,
			fieldHint: BLOCK_MISMATCH_HINT,
		});
	}
	if (
		item.unit !== null &&
		item.blockUnitFrom !== null &&
		item.blockUnitTo !== null &&
		(item.unit < item.blockUnitFrom || item.unit > item.blockUnitTo)
	) {
		issues.push({
			fields: ['unit', 'blockId'],
			message: `Unit ${item.unit} doesn't match the selected block's range (${item.blockUnitFrom}-${item.blockUnitTo}) — check the block`,
			fieldHint: BLOCK_MISMATCH_HINT,
		});
	}
	if (item.unit !== null && item.blockUnitNumberPattern === 'odd' && item.unit % 2 === 0) {
		issues.push({ fields: ['unit', 'blockId'], message: 'This block uses odd unit numbers only' });
	}
	if (item.unit !== null && item.blockUnitNumberPattern === 'even' && item.unit % 2 !== 0) {
		issues.push({ fields: ['unit', 'blockId'], message: 'This block uses even unit numbers only' });
	}

	if (item.workCategory !== 'normal' && item.isClientBillable) {
		issues.push({
			fields: ['workCategory'],
			message: 'Free Redo, Rectify and Fabrication cannot be client billable',
		});
	}
	if (!item.isWorkerCreditable) {
		issues.push({ fields: [], message: 'Initial workflow requires worker-creditable work' });
	}
	if (item.userId !== null && item.helperUserIds.includes(item.userId)) {
		issues.push({ fields: ['userId'], message: 'Credited worker cannot also be a helper' });
	}

	return issues;
};

export const getWorkItemReviewIssues = (item: ReviewableWorkItem, options: WorkItemReviewOptions = {}): string[] =>
	getWorkItemFieldIssues(item, options).map((issue) => issue.message);

/** Which form controls should show a red/error border for this item. */
export const getWorkItemInvalidFields = (
	item: ReviewableWorkItem,
	options: WorkItemReviewOptions = {}
): Set<WorkItemIssueField> => new Set(getWorkItemFieldIssues(item, options).flatMap((issue) => issue.fields));

export const isWorkItemReady = (item: ReviewableWorkItem, options: WorkItemReviewOptions = {}): boolean =>
	getWorkItemReviewIssues(item, options).length === 0;
